import { useState, useCallback, useEffect, useRef } from "react";
import * as Icons from "lucide-react";

// Visionneuse plein écran réutilisable : zoom, navigation, glisser pour déplacer.
// Utilisation :
//   const lb = useLightbox();
//   <img onClick={() => lb.open(images, index)} />
//   {lb.node}
// images = [{ src, caption }]
export function useLightbox() {
  const [state, setState] = useState(null); // { images, index }
  const open = useCallback((images, index = 0) => {
    const list = (images || []).filter((im) => im && im.src);
    if (!list.length) return;
    setState({ images: list, index: Math.max(0, Math.min(index, list.length - 1)) });
  }, []);
  const close = useCallback(() => setState(null), []);
  const node = state ? (
    <LightboxView images={state.images} startIndex={state.index} onClose={close} />
  ) : null;
  return { open, close, node };
}

function LightboxView({ images, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const current = images[index];

  const reset = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);
  const go = useCallback((d) => {
    setIndex((i) => (i + d + images.length) % images.length);
    reset();
  }, [images.length, reset]);
  const zoomBy = useCallback((f) => setZoom((z) => Math.min(5, Math.max(1, +(z + f).toFixed(2)))), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && images.length > 1) go(1);
      else if (e.key === "ArrowLeft" && images.length > 1) go(-1);
      else if (e.key === "+" || e.key === "=") zoomBy(0.5);
      else if (e.key === "-") zoomBy(-0.5);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, zoomBy, onClose, images.length]);

  const onWheel = (e) => { zoomBy(e.deltaY < 0 ? 0.3 : -0.3); };
  const onDown = (e) => {
    if (zoom <= 1) return;
    drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };
  const onMove = (e) => {
    if (!drag.current) return;
    setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const onUp = () => { drag.current = null; };
  const toggleZoom = () => { if (zoom > 1) reset(); else setZoom(2); };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/92 backdrop-blur-sm"
      data-testid="lightbox"
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
    >
      {/* Fermer */}
      <button onClick={onClose} data-testid="lightbox-close"
        className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20">
        <Icons.X className="h-5 w-5" />
      </button>

      {/* Compteur */}
      {images.length > 1 && (
        <span className="absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/90" data-testid="lightbox-counter">
          {index + 1} / {images.length}
        </span>
      )}

      {/* Précédent / Suivant */}
      {images.length > 1 && (
        <>
          <button onClick={() => go(-1)} data-testid="lightbox-prev"
            className="absolute left-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-4">
            <Icons.ChevronLeft className="h-6 w-6" />
          </button>
          <button onClick={() => go(1)} data-testid="lightbox-next"
            className="absolute right-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-4">
            <Icons.ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Image */}
      <div className="flex h-full w-full items-center justify-center overflow-hidden px-4 py-16" onWheel={onWheel}>
        <img
          src={current.src}
          alt={current.caption || ""}
          data-testid="lightbox-image"
          draggable={false}
          onMouseDown={onDown}
          onDoubleClick={toggleZoom}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            cursor: zoom > 1 ? (drag.current ? "grabbing" : "grab") : "zoom-in",
            transition: drag.current ? "none" : "transform 0.15s ease-out",
          }}
          className="max-h-full max-w-full select-none rounded-lg object-contain"
        />
      </div>

      {/* Légende */}
      {current.caption && (
        <p className="absolute bottom-16 left-1/2 z-10 max-w-[90%] -translate-x-1/2 rounded-lg bg-black/50 px-3 py-1.5 text-center text-sm text-white" data-testid="lightbox-caption">
          {current.caption}
        </p>
      )}

      {/* Contrôles de zoom */}
      <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/10 px-2 py-1.5">
        <button onClick={() => zoomBy(-0.5)} disabled={zoom <= 1} data-testid="lightbox-zoom-out"
          className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20 disabled:opacity-40">
          <Icons.ZoomOut className="h-5 w-5" />
        </button>
        <span className="min-w-[3ch] text-center text-xs font-medium text-white/90">{Math.round(zoom * 100)}%</span>
        <button onClick={() => zoomBy(0.5)} disabled={zoom >= 5} data-testid="lightbox-zoom-in"
          className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20 disabled:opacity-40">
          <Icons.ZoomIn className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
